import React, { useState, useEffect } from "react";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import {
	CheckCircleIcon,
	MapPinIcon,
	ClockIcon,
	CalendarIcon,
	UserIcon,
	BuildingIcon,
} from "lucide-react";

// Components
import {
	getEndTime,
	transformStartTime,
} from "../../../components/formattedTime";
import ContinueBtn from "../../../components/ContinueBtn";
import ProgressStepper from "../../../components/ProgressStepper";
import logo from "../../../images/logoAndSponsor.png";
import {
	temproraryReservation,
	getBoothAvailability,
} from "../../../services/api";
import { capitalizeFirstLetter } from "../../../utils/textFormatter";
import { checkPastTime } from "../../../utils/timeFormatter";

// Env
const env = process.env.REACT_APP_ENV;

export default function BoothConfirmation() {
	const navigate = useNavigate();
	const location = useLocation();
	const { locationId } = useParams();
	const { startTime, duration, date, boothId, preSelectedBooth } =
		location.state || {};

	if (!startTime || !duration)
		navigate(`/${locationId}/reservation/time-scheduler`);
	if (checkPastTime(startTime, locationId))
		navigate(`/${locationId}/reservation/time-scheduler`);

	const [errorMessage, setErrorMessage] = useState(false);
	const [pageLoading, setPageLoading] = useState(false);
	const [isValidatingAvailability, setIsValidatingAvailability] =
		useState(false);

	// Use the pre-selected booth data
	const booth = preSelectedBooth;
	const selectedBooth = boothId;
	const selectedBuilding = booth?.boxPlacement;
	const boothType = booth?.boothType || "single";

	// Redirect if we don't have booth data
	useEffect(() => {
		if (!booth || !selectedBooth) {
			navigate(`/${locationId}/reservation/time-scheduler`);
		}
	}, [booth, selectedBooth, navigate, locationId]);

	const displayErrorMessage = (message) => {
		setErrorMessage(message);
		setTimeout(() => {
			setErrorMessage(false);
		}, 7000);
	};

	const validateAvailability = async () => {
		try {
			setIsValidatingAvailability(true);

			// Convert startTime to 24-hour format for API comparison
			const convertTo24Hour = (time12) => {
				const [time, period] = time12.split(" ");
				let [hours, minutes] = time.split(":");
				hours = parseInt(hours);

				if (period.toLowerCase() === "pm" && hours !== 12) {
					hours += 12;
				} else if (period.toLowerCase() === "am" && hours === 12) {
					hours = 0;
				}

				return `${String(hours).padStart(2, "0")}:${minutes}`;
			};

			const response = await getBoothAvailability({
				boothId: selectedBooth,
				locationId: booth.location._id || booth.location,
				date: date,
				duration: duration,
			});

			const startTime24 = convertTo24Hour(startTime);
			const isAvailable = response.availableTimeSlots.includes(startTime24);

			setIsValidatingAvailability(false);
			return isAvailable;
		} catch (error) {
			setIsValidatingAvailability(false);
			console.error("Error validating availability:", error);
			return false;
		}
	};

	const continueToStartSession = async () => {
		try {
			// First validate availability
			const isAvailable = await validateAvailability();

			if (!isAvailable) {
				displayErrorMessage(
					"This time slot is no longer available. Please select a different time."
				);
				// Navigate back to time scheduler after a delay
				setTimeout(() => {
					navigate(
						`/${
							booth.location._id || booth.location
						}/reservation/time-scheduler`,
						{
							state: {
								boothId: boothId,
								preSelectedBooth: preSelectedBooth,
								locationId: booth.location._id || booth.location,
							},
						}
					);
				}, 3000);
				return;
			}

			setPageLoading(true);
			const session = await temproraryReservation(
				selectedBooth,
				selectedBuilding,
				startTime,
				getEndTime(startTime, duration),
				booth.location._id || booth.location, // Use booth's location reference
				date
			);
			setPageLoading(false);

			navigate(
				`/${booth.location._id || booth.location}/reservation/start-session`,
				{
					state: {
						sessionId: session.sessionId,
						boothId: selectedBooth,
						boothType: boothType,
						boxPlacement: selectedBuilding,
						lockId: booth.lockId, // Include lockId from booth data
						saltoLocationId: booth.location?.saltoLocationId, // Include SALTO location ID
						time: transformStartTime(startTime),
						duration: duration,
						endTime: getEndTime(startTime, duration),
						date: date,
						boothData: booth, // Pass full booth data for SALTO integration
					},
				}
			);
		} catch (error) {
			setPageLoading(false);
			if (error.message === "The booth is no longer available") {
				displayErrorMessage(
					"The booth is no longer available for this time slot. Redirecting to time selection..."
				);
				// Navigate back to time scheduler after a delay
				setTimeout(() => {
					navigate(
						`/${
							booth.location._id || booth.location
						}/reservation/time-scheduler`,
						{
							state: {
								boothId: boothId,
								preSelectedBooth: preSelectedBooth,
								locationId: booth.location._id || booth.location,
							},
						}
					);
				}, 3000);
			} else {
				env === "development" && console.log("Error", error.message);
				displayErrorMessage("An error occurred while reserving the booth");
			}
		}
	};

	const goBack = () => {
		navigate(
			`/${booth.location._id || booth.location}/reservation/time-scheduler`,
			{
				state: {
					boothId: boothId,
					preSelectedBooth: preSelectedBooth,
					locationId: booth.location._id || booth.location,
				},
			}
		);
	};

	if (!booth) return null;

	return (
		<section className="flex flex-col justify-center mb-20 pb-10 md:px-mdPagePadding md:mx-smPagePadding lg:px-lgPagePadding lg:mx-lgPagePadding xl:px-xlPagePadding xl:mx-xlPagePadding">
			{errorMessage && (
				<div className="fixed top-0 left-0 right-0 m-8 text-black text-sm text-center py-3 z-50 border border-red-300 rounded-secondary bg-red-50 shadow-lg animate-slideInDown">
					<div className="flex flex-col items-center">
						<span className="mt-2">{errorMessage}</span>
					</div>
				</div>
			)}

			{/* Progress Stepper and Logo */}
			<div className="mt-10">
				<ProgressStepper currentStep={2} />
			</div>
			<div className="flex flex-col justify-center items-center px-2 py-8">
				<img src={logo} alt="logo" className="w-40 h-auto object-contain" />
			</div>

			{/* Booth Confirmation Header */}
			<div className="text-center mb-8">
				<div className="flex items-center justify-center mb-4">
					<CheckCircleIcon className="w-8 h-8 text-green-600 mr-2" />
					<h1 className="text-2xl font-semibold text-gray-900">Booth Found!</h1>
				</div>
				<p className="text-gray-600">Confirm your reservation for this booth</p>
			</div>

			{/* Booth Details Card */}
			<div className="mx-smPagePadding mb-8">
				<div className="bg-white border border-gray-200 rounded-lg shadow-sm p-6">
					{/* Booth Header */}
					<div className="flex items-center justify-between mb-6">
						<div className="flex items-center">
							<div className="w-12 h-12 bg-black text-white rounded-lg flex items-center justify-center text-xl font-bold mr-4">
								{selectedBooth}
							</div>
							<div>
								<h2 className="text-lg font-semibold text-gray-900">
									{capitalizeFirstLetter(boothType)} Booth #{selectedBooth}
								</h2>
								<div className="flex items-center text-sm text-gray-500">
									<UserIcon className="w-4 h-4 mr-1" />
									{boothType === "single" ? "1 Person" : "Multiple People"}
								</div>
							</div>
						</div>
					</div>

					{/* Booth Details */}
					<div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
						<div className="flex items-center text-sm text-gray-600">
							<BuildingIcon className="w-4 h-4 mr-2 text-gray-400" />
							<span>{selectedBuilding}</span>
						</div>
						<div className="flex items-center text-sm text-gray-600">
							<MapPinIcon className="w-4 h-4 mr-2 text-gray-400" />
							<span>{booth.location?.name || "Loading location..."}</span>
						</div>
					</div>

					{/* Reservation Details */}
					<div className="border-t pt-4">
						<h3 className="font-medium text-gray-900 mb-3">
							Reservation Details
						</h3>
						<div className="grid grid-cols-1 md:grid-cols-3 gap-4">
							<div className="flex items-center text-sm">
								<CalendarIcon className="w-4 h-4 mr-2 text-gray-400" />
								<span>{date === 0 ? "Today" : "Tomorrow"}</span>
							</div>
							<div className="flex items-center text-sm">
								<ClockIcon className="w-4 h-4 mr-2 text-gray-400" />
								<span>{transformStartTime(startTime)}</span>
							</div>
							<div className="flex items-center text-sm">
								<ClockIcon className="w-4 h-4 mr-2 text-gray-400" />
								<span>{duration} minutes</span>
							</div>
						</div>
					</div>

					{/* Time Summary */}
					<div className="mt-4 p-3 bg-gray-50 rounded-lg">
						<div className="text-center">
							<span className="text-sm text-gray-600">Session Time: </span>
							<span className="font-medium text-gray-900">
								{transformStartTime(startTime)} -{" "}
								{getEndTime(startTime, duration)}
							</span>
						</div>
					</div>
				</div>
			</div>

			{/* Action Buttons */}
			<div className="mx-smPagePadding">
				<ContinueBtn
					btnBack={true}
					disabled={pageLoading || isValidatingAvailability}
					onClick={continueToStartSession}
					onBack={goBack}
					buttonText={
						isValidatingAvailability
							? "Checking availability..."
							: pageLoading
							? "Reserving..."
							: "Confirm Reservation"
					}
				/>
			</div>

			{/* Loading Overlay */}
			{pageLoading && (
				<div className="fixed inset-0 bg-white/80 flex items-center justify-center z-50">
					<div className="flex flex-col items-center">
						<div className="w-10 h-10 border-t-2 border-b-2 border-gray-900 rounded-full animate-spin"></div>
						<p className="mt-4 text-gray-600">Reserving your booth...</p>
					</div>
				</div>
			)}
		</section>
	);
}
